/* « Emplacement » — where a support stands, picked from the workspace's list (wireframes-fully ruling § 6, WF-042; Technical's
   0039; lib-pack-23's lib/organisation). It replaces the free-text place once useOrganisationReady() is true: « Que souhaitez-vous
   installer ? » (WF-078), the designer's « Où ? » and, for a support that exists, the « Organisation interne » card of its
   « Modèle et design » tab (row data: PATCH { placeId }, never the design, never the QR). Who writes the list adds one inline
   (« Ajouter un emplacement… ») and it is picked; staff and a manager of some establishments pick from the list (0039 P4 is
   retired for the app). Never shown to a customer, never a rule of the parcours: it groups the supports and Résultats.
   No gift check on the name (0039 P3: a spot in the shop). Until then the callers keep today's free text. */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Input, Select } from '@/ui'
import { useLocations } from '@/lib/api'
import { addPlace, canWritePlaceList, setSupportPlace, supportPlace, usePlaces, useOrganisationStore, PLACE_NAME_MAX } from '@/lib/organisation'
import { editRefusalLines, settled, writeMark } from '@/lib/writes'

const NEW = '__nouveau'

/** The select (and the inline add). Controlled with `value`/`onChange` before the support exists; with `id` it writes the
    support's emplacement at once and says a refusal once the server answers. */
export function EmplacementPicker({ id, value, onChange, canEdit, help }: {
  id?: string | null
  value?: string | null; onChange?: (placeId: string | null, name: string | null) => void
  canEdit: boolean
  help?: string
}) {
  const list = usePlaces()
  useOrganisationStore()
  const { data: locations } = useLocations()
  const listWriter = canWritePlaceList((locations ?? []).map(l => l.id))
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState('')
  const [refusal, setRefusal] = useState<string | null>(null)
  const chosen = id ? supportPlace(id)?.id ?? null : (value ?? null)
  const hear = (mark: number) => { void settled(mark).then(no => { if (no) setRefusal(editRefusalLines(no)[0]) }) }
  const put = (placeId: string | null, name: string | null, mark = writeMark()) => {
    setRefusal(null)
    if (id) { const r = setSupportPlace(id, placeId); if (r) { setRefusal(r); return } hear(mark) }
    onChange?.(placeId, name)
  }
  const create = () => {
    const mark = writeMark()
    const r = addPlace(draft)
    if (typeof r === 'string') { setRefusal(r); return }
    setDraft(''); setAdding(false)
    put(r.id, r.name, mark)
    if (!id) hear(mark)
  }
  const name = list.find(p => p.id === chosen)?.name ?? null
  if (!canEdit) return <p className="f-help" style={{ margin: 0 }} data-testid="emplacement-lecture">Emplacement&nbsp;: {name ?? 'à préciser'}</p>
  return (
    <div data-testid="emplacement-picker" style={{ display: 'grid', gap: 8 }}>
      <label className="field" style={{ margin: 0 }}><span className="f-label">Emplacement</span>
        <Select aria-label="Emplacement" data-testid="emplacement-choix" value={adding ? NEW : chosen ?? ''} style={{ maxWidth: 340 }}
          onChange={e => { const v = e.target.value; if (v === NEW) { setAdding(true); setRefusal(null); return } setAdding(false); put(v || null, list.find(p => p.id === v)?.name ?? null) }}>
          <option value="">Choisir ou préciser plus tard</option>
          {list.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          {listWriter && <option value={NEW}>Ajouter un emplacement…</option>}
        </Select></label>
      {help && <span className="f-help">{help}</span>}
      {adding && listWriter && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <Input aria-label="Nouvel emplacement" placeholder="Table 6, vitrine, caisse…" value={draft} maxLength={PLACE_NAME_MAX + 16} autoFocus data-testid="emplacement-nouveau"
            style={{ flex: '1 1 180px', minWidth: 0, maxWidth: 280 }}
            onChange={e => { setDraft(e.target.value); setRefusal(null) }} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); create() } }} />
          <Button size="sm" onClick={create} disabled={!draft.trim()} data-testid="emplacement-ajouter">Ajouter</Button>
          <Link to="/parametres/organisation" className="f-help">Gérer les emplacements</Link>
        </div>
      )}
      {refusal && <p className="f-help err" role="alert" style={{ margin: 0 }} data-testid="emplacement-refus">{refusal}</p>}
    </div>
  )
}
